/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import InjectableComponent from '../Utility/Component/ComponentReference/InjectableComponent';
import { ReferenceGroup } from '../Utility/Component/ComponentReference/ReferenceGroup';
import ObservableIntComponent from '../Utility/Component/Observable/ObservableIntComponent';
import Decorators from '../Utility/Decorators';
import { PaylineEvent } from './PaylineEvent';
import PaylineManager from './PaylineManager';

/** @hidden */
// tslint:disable-next-line:typedef
const ccclass = cc._decorator.ccclass;
/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Supporting class for [[MechPaylineManager]] that represents the pairing of
 * a payline ID with a visual payline in the scene.
 */
@ccclass("PaylineMapping")
export class PaylineMapping
{
    /** The paylineId from ISlotGamePlayResult to associate with this visual representation of a payline. */
    @property({
        tooltip: "The paylineId from ISlotGamePlayResult to associate with this visual " +
        "representation of a payline.",
        type: cc.Integer
    })
    public PaylineId: number = 0;

    /** The node which contains the visual representation of the payline. */
    @property({
        tooltip: "The node which contains the visual representation of the payline.",
        type: cc.Animation
    })
    public Payline: cc.Animation = null;
}

/**
 * Implementation of [[PaylineManager]] that represents the types of paylines used
 * in mechanical stepper style games.
 */
@ccclass
export default class MechPaylineManager extends PaylineManager
{
    /** @hidden */
    @property({ type: [PaylineMapping], visible: true })
    private _paylines: PaylineMapping[] = [];
    /** The mappings of visual payline to paylineId used in this stepper machine. */
    public get Paylines(): PaylineMapping[] { return this._paylines; }

    /** @hidden */
    protected linesObservableRef: InjectableComponent<ObservableIntComponent>
        = new InjectableComponent(ReferenceGroup.MainGame, 'BetManager.Lines');

    private get _currentPayline(): PaylineMapping
    {
        // Find our payline.
        const paylineId: number = this.PaylineIds[this.currentPaylineIndex];
        return this._paylines.find(line => line.PaylineId === paylineId);
    }

    private _paylineAnimationMap: Map<PaylineMapping, cc.Animation[]> = new Map();

    /** @hidden */
    protected setContexts(): void
    {
        super.setContexts();
        this.linesObservableRef.SetContext(ObservableIntComponent);
    }

    /** @hidden */
    protected start(): void
    {
        super.start();

        this.linesObservableRef.WithValue(observable => observable.OnChanged(this.activeLinesChanged));
        this.configurePaylineAnimations();
    }

    protected onDestroy(): void
    {
        this.linesObservableRef.WithValue(observable => observable.OffChanged(this.activeLinesChanged));
        super.onDestroy();
    }

    public ClearPaylines(): void
    {
        super.ClearPaylines();
        this.Paylines.forEach(payline => this.clearPaylineAnimation(payline));
    }

    /** Adds and removes visual paylines based on the active lines for the current wager. */
    @Decorators.BindThis()
    protected activeLinesChanged = (event: cc.Event.EventCustom): void =>
    {
        if (isNaN(event.detail)) { event.detail = 0; }

        for (let paylineMapping of this._paylines)
        {
            if (paylineMapping.PaylineId < event.detail)
            {
                paylineMapping.Payline.node.active = true;
            }
            else
            {
                paylineMapping.Payline.node.active = false;
            }
        }
    }

    /** Advances the active payline and shows its visual effect. */
    protected cyclePayline(): void
    {
        this.clearPaylineAnimation(this._currentPayline);
        super.cyclePayline();
    }

    /** Animates the current payline and its associated symbols. */
    protected showCurrentPayline(): void
    {
        // The reel offsets for this payline (0: center, 1: bottom, -1: top)
        let offsetSet: number[] = this.paylineOffsets[this.currentPaylineIndex];

        // Notify the reel symbols of payline cycle.
        this.paylineOffsets
                .filter((x) => x !== offsetSet)
                .forEach((x) => this.sendPaylineEvent.call(this, PaylineEvent.OnClear, [x]), this);

        const isScatter: boolean = offsetSet.length > this._reelManagerRef.Value.ReelCount;
        const paylineStrings: string = this.result.paylinePrizeString[this.currentPaylineIndex];

        const maxSymbolAnimDuration: number =
            this.animatePaylineSymbols(PaylineEvent.OnActive, offsetSet, isScatter, paylineStrings);
        this.currentCycleTime = maxSymbolAnimDuration > this._cycleTime ? maxSymbolAnimDuration : this._cycleTime;

        const payline: PaylineMapping = this._currentPayline;

        if (payline)
        {
            if (payline.Payline.defaultClip.duration > this.currentCycleTime)
            {
                this.currentCycleTime = payline.Payline.defaultClip.duration;
            }
            payline.Payline.play();
        }

        this.buildPaylineMessage(this.currentPaylineIndex);
        this.applyDTModeToCycleTime();

        // Schedule the next payline cycle.
        this.scheduleOnce(() => this.cyclePayline(), this.currentCycleTime);
    }

    private clearPaylineAnimation(payline: PaylineMapping): void
    {
        if (!payline) { return; }

        const animations: cc.Animation[] = this._paylineAnimationMap.get(payline);

        if (!animations || animations.length < 1) { return; }

        animations.forEach(anim =>
            {
                anim.setCurrentTime(0);
                anim.stop();
            });
    }

    private configurePaylineAnimations(): void
    {
        this.Paylines.forEach(payline =>
            {
                const paylineAnimations: cc.Animation[] =
                [
                    payline.Payline,
                    ...payline.Payline.getComponentsInChildren(cc.Animation)
                ];

                const animations: cc.Animation[] = this._paylineAnimationMap.get(payline);
                if (!animations)
                {
                    this._paylineAnimationMap.set(payline, paylineAnimations);
                    return;
                }
                animations.push(...paylineAnimations);
            });
    }
}
